import type { AIToolsConfigService } from '@affine/core/modules/ai-button';
import type {
  AIModelService,
  ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import type { WorkspaceNavigationPort } from '@affine/core/modules/streamlain/copilot/workspace-navigation-executor';
import type { AppThemeService } from '@affine/core/modules/theme';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { I18n } from '@affine/i18n';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import {
  DocModeProvider,
  type FeatureFlagService,
  type NotificationService,
} from '@blocksuite/affine/shared/services';
import { type EditorHost, ShadowlessElement } from '@blocksuite/affine/std';
import type { BaseSelection, ExtensionType } from '@blocksuite/affine/store';
import {
  streamLainAnimationController,
  streamLainOverlayKeyframes,
} from '@blocksuite/affine-shared/utils';
import { ArrowDownBigIcon as ArrowDownIcon } from '@blocksuite/icons/lit';
import { css, html, nothing, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';

import type { WorkspaceDocSyncEngine } from '../../../../modules/streamlain/stream-objects';
import { type AIError, AIProvider, UnauthorizedError } from '../../provider';
import {
  aiChatIdentityTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';
import { getAIChatOnlineStatus } from '../../utils/chat-connectivity';
import { fetchChatKeysCheck } from '../../utils/chat-keys-check';
import {
  isReasoningModeActive,
  resolveReasoningMode,
  resolveReasoningModel,
} from '../../utils/reasoning-mode';
import { mergeStreamObjects } from '../../utils/stream-objects';
import type { DocDisplayConfig } from '../ai-chat-chips';
import type {
  CanvasAIChatContextMode,
  ChatContextValue,
} from '../ai-chat-content/type';
import {
  AIChatEmptyStateStyles,
  renderAIChatEmptyStateContent,
} from '../ai-chat-empty-state';
import type { AIAgentChoice, AINetworkSearchConfig } from '../ai-chat-input';
import { resolveCopilotRetrievalPlan } from '../ai-chat-input/retrieval-mode';
import { renderAssistantIdentity } from '../ai-message-content/assistant-identity';
import { isGoalBusy } from '../goal-tree/goal-run-status';
import type { GoalPlanHandle } from '../goal-tree/goal-tree-context';
import { goalActivityText } from '../goal-tree/goal-tree-view-model';
import {
  CHAT_END_TOLERANCE,
  EndJump,
  followsAfterScroll,
} from './chat-scroll-follow';
import { MessageKeys } from './message-keys';
import { shouldHideChatMessage } from './message-visibility';
import { syncMissingMessageIds } from './sync-message-ids';
import {
  type HistoryMessage,
  isChatAction,
  isChatMessage,
  StreamObjectSchema,
} from './type';

const INITIAL_VISIBLE_MESSAGES = 48;
const PREPEND_VISIBLE_MESSAGES = 24;
const LOAD_MORE_SCROLL_THRESHOLD = 96;

export class AIChatMessages extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${AIChatEmptyStateStyles}

    ai-chat-messages {
      position: relative;
    }

    .chat-panel-messages-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
      min-height: 100%;
      position: relative;
      box-sizing: border-box;
      padding-bottom: 8px;
    }

    chat-panel-assistant-message,
    chat-panel-user-message {
      display: contents;
    }

    .user-info {
      ${aiChatIdentityTypography}
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 4px;
      color: var(--streamlain-v2-text-primary);
      user-select: none;
    }

    .messages-placeholder {
      width: 100%;
      position: absolute;
      z-index: 1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }
    .independent-mode .messages-placeholder {
      position: static;
      transform: none;
    }

    /* Takes no room in the history: the button floats above its last lines. */
    .chat-end-anchor {
      position: sticky;
      bottom: 0;
      z-index: 1;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      height: 0;
      pointer-events: none;
    }

    .down-indicator {
      flex: 0 0 auto;
      box-sizing: border-box;
      width: 28px;
      height: 28px;
      margin: 0 0 12px;
      padding: 0;
      border-radius: 50%;
      border: 0.5px solid var(--streamlain-v2-layer-insideBorder-border);
      background-color: var(--streamlain-v2-layer-background-primary);
      box-shadow: var(--streamlain-v2-shadow-level2);
      color: var(--streamlain-v2-icon-primary);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      pointer-events: auto;
    }

    .down-indicator:focus-visible {
      outline: 2px solid var(--streamlain-v2-brand-primary);
      outline-offset: 2px;
    }

    .down-indicator svg {
      width: 16px;
      height: 16px;
    }

    .chat-history-window-sentinel {
      height: 1px;
      flex: 0 0 1px;
      pointer-events: none;
    }

    .goal-activity {
      display: grid;
      gap: 0;
      color: var(--streamlain-v2-text-primary);
      font-family: var(--streamlain-font-family, inherit);
      animation: goal-activity-reveal 180ms ease-out both;
    }

    .goal-activity-status {
      ${aiChatStatusTypography}
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 34px;
      padding: 8px 10px;
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-secondary);
      color: var(--streamlain-v2-text-secondary);
    }

    @keyframes goal-activity-reveal {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .goal-activity {
        animation: none;
      }
    }

    .goal-activity-dots {
      display: inline-flex;
      gap: 3px;
      flex: 0 0 auto;
    }

    .goal-activity-dots i {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: currentColor;
      animation: goal-activity-dot 1.2s ease-in-out infinite;
    }

    .goal-activity-dots i:nth-child(2) {
      animation-delay: 0.15s;
    }

    .goal-activity-dots i:nth-child(3) {
      animation-delay: 0.3s;
    }

    @keyframes goal-activity-dot {
      0%,
      60%,
      100% {
        opacity: 0.35;
        transform: translateY(0);
      }
      30% {
        opacity: 1;
        transform: translateY(-2px);
      }
    }
  `;

  @state()
  accessor _selectionValue: BaseSelection[] = [];

  /**
   * The history keeps the newest message in sight. A reader who leaves the end
   * turns it off; the end button or the end itself turns it on again.
   */
  @state()
  accessor following = true;

  /** The end button stays in the page until its way out has played. */
  @state()
  private accessor _endButtonMounted = false;

  @state()
  accessor avatarUrl = '';

  /**
   * Whether AI chat keys are configured (user, workspace, or server)
   * null = checking, true = has keys, false = no keys
   */
  @state()
  accessor hasKeys: boolean | null = null;

  @property({ attribute: false })
  accessor onOpenSettings: (() => void) | undefined;

  @property({ attribute: false })
  accessor noKeysTitle = '';

  @property({ attribute: false })
  accessor noKeysDescription = '';

  @property({ attribute: false })
  accessor noKeysButtonText = '';

  @property({ attribute: false })
  accessor aiStatusReason: string | null = null;

  @property({ attribute: false })
  accessor aiStatusIsOwner = false;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor canvasAIChatContextMode: CanvasAIChatContextMode = 'lineage-only';

  @property({ attribute: false })
  accessor messages!: HistoryMessage[];

  @property({ attribute: false })
  accessor goalPlan: GoalPlanHandle | null = null;

  @property({ attribute: false })
  accessor host: EditorHost | null | undefined;

  @property({ attribute: false })
  accessor workspaceId!: string;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor isHistoryLoading!: boolean;

  @property({ attribute: false })
  accessor chatContextValue!: ChatContextValue;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;

  @property({ attribute: false })
  accessor createSession!: () => Promise<
    CopilotChatHistoryFragment | undefined
  >;

  @property({ attribute: false })
  accessor updateContext!: (context: Partial<ChatContextValue>) => void;

  @property({ attribute: false })
  accessor extensions!: ExtensionType[];

  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;

  @property({ attribute: false })
  accessor affineThemeService!: AppThemeService;

  @property({ attribute: false })
  accessor notificationService!: NotificationService;

  @property({ attribute: false })
  accessor networkSearchConfig!: AINetworkSearchConfig;

  @property({ attribute: false })
  accessor agentChoice!: AIAgentChoice;

  @property({ attribute: false })
  accessor docDisplayService!: DocDisplayConfig;

  @property({ attribute: false })
  accessor aiToolsConfigService!: AIToolsConfigService;

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined;

  @property({ attribute: false })
  accessor workspaceNavigationPort: WorkspaceNavigationPort | undefined;

  @property({
    type: String,
    attribute: 'data-testid',
    reflect: true,
  })
  accessor testId = 'chat-panel-messages';

  private readonly _messageKeys = new MessageKeys();

  private _visibleStartIndex = 0;

  private _lastFilteredItemsLength = 0;

  private _pendingBottomJump = false;

  private _restoringScrollAnchor = false;

  private _messagesResizeObserver: ResizeObserver | null = null;

  private _observedMessagesContainer: HTMLElement | null = null;

  private _resizeScrollFrame: number | null = null;

  private _lastScrollTop = 0;

  private _touching = false;

  private readonly _endJump = new EndJump();

  private _endButtonShown = false;

  private _filterMessages(isLoading: boolean): HistoryMessage[] {
    return this.messages.filter((item, index) => {
      const isLast = index === this.messages.length - 1;
      if (!isChatMessage(item)) return true;
      const chatMessages = this.messages.filter(isChatMessage);
      return !shouldHideChatMessage(item, chatMessages, isLast, isLoading);
    });
  }

  private _syncVisibleWindow(filteredLength: number) {
    const previousLength = this._lastFilteredItemsLength;
    const lengthChanged = previousLength !== filteredLength;

    if (!lengthChanged) return;

    const initialStart = Math.max(0, filteredLength - INITIAL_VISIBLE_MESSAGES);
    const didShrink = filteredLength < previousLength;
    const firstLoad = previousLength === 0;
    const wasAtBottom = this.following;

    if (firstLoad || didShrink || this._visibleStartIndex > filteredLength) {
      this._visibleStartIndex = initialStart;
      this._pendingBottomJump = filteredLength > 0;
    } else if (wasAtBottom) {
      const visibleCount = Math.max(
        INITIAL_VISIBLE_MESSAGES,
        previousLength - this._visibleStartIndex
      );
      this._visibleStartIndex = Math.max(0, filteredLength - visibleCount);
      this._pendingBottomJump = true;
    }

    this._lastFilteredItemsLength = filteredLength;
  }

  private _loadPreviousWindow() {
    if (
      this._visibleStartIndex <= 0 ||
      this._restoringScrollAnchor ||
      this._pendingBottomJump ||
      this.isHistoryLoading
    ) {
      return;
    }

    const previousScrollHeight = this.scrollHeight;
    const previousScrollTop = this.scrollTop;
    this._visibleStartIndex = Math.max(
      0,
      this._visibleStartIndex - PREPEND_VISIBLE_MESSAGES
    );
    this._restoringScrollAnchor = true;
    this.requestUpdate();
    this.updateComplete
      .then(() => {
        this.scrollTop =
          this.scrollHeight - previousScrollHeight + previousScrollTop;
      })
      .finally(() => {
        this._restoringScrollAnchor = false;
      })
      .catch(console.error);
  }

  private _jumpToEnd() {
    this.following = true;
    this._endJump.start({
      toEnd: () => {
        streamLainAnimationController.cancel(this);
        this.scrollTop = this.scrollHeight;
      },
      follows: () => this.following,
      rendered: () => this.updateComplete,
      nextFrame: step => requestAnimationFrame(step),
    });
  }

  private get _isReasoningActive() {
    return isReasoningModeActive(
      this._selectedAIModel,
      this._selectedReasoningMode
    );
  }

  private get _selectedReasoningMode(): ReasoningModeId | undefined {
    return resolveReasoningMode(
      this._selectedAIModel,
      this.agentChoice.enabled.value,
      this.agentChoice.effort.value
    );
  }

  private get _selectedAIModel() {
    return resolveReasoningModel(
      this.aiModelService.models.value,
      this.agentChoice.modelId.value
    );
  }

  /**
   * Check if AI keys are configured (user, workspace, or server)
   */
  private readonly _checkAIKeys = async () => {
    try {
      const data = await fetchChatKeysCheck(this.workspaceId);
      this.hasKeys = data.hasKeys;
      this.aiStatusReason = data.reason;
      this.aiStatusIsOwner = data.isOwner;
    } catch (error) {
      console.error('[AIChatMessages] Failed to check AI keys:', error);
      // On error, assume keys are available
      this.hasKeys = true;
    }
  };

  /**
   * Render the "No Keys Configured" card for empty thread
   * Replaces the empty-chat placeholder when no keys are configured.
   */
  private _renderNoKeysCardIfNeeded() {
    return html`<ai-no-keys-card
      .title=${this.noKeysTitle}
      .description=${this.noKeysDescription}
      .buttonText=${this.noKeysButtonText}
      .reason=${this.aiStatusReason}
      .isOwner=${this.aiStatusIsOwner}
      .onOpenSettings=${this.onOpenSettings}
    ></ai-no-keys-card>`;
  }

  /**
   * Render the "No Keys Configured" card for existing thread with messages
   * Shows as additional info (like Confirmation Card pattern)
   */
  private _renderNoKeysCardForExistingThread() {
    // Only show when:
    // 1. There ARE messages (existing thread)
    // 2. Keys check confirms no keys OR there's an error (likely key-related)
    // 3. Not loading history
    // 4. No AI response yet (if AI responded, keys are working)
    const hasAIResponse = this.messages.some(
      m => 'role' in m && m.role === 'assistant'
    );
    const hasUserMessage = this.messages.some(
      m => 'role' in m && m.role === 'user'
    );
    const { status } = this.chatContextValue;
    const hasError = status === 'error';

    // Don't show if: no messages, loading, or AI already responded
    if (this.messages.length === 0 || this.isHistoryLoading || hasAIResponse) {
      return nothing;
    }

    // Show if: user sent message and (no keys confirmed OR error occurred)
    const shouldShow = hasUserMessage && (this.hasKeys === false || hasError);

    if (!shouldShow) {
      return nothing;
    }

    return html`<ai-no-keys-card
      .title=${this.noKeysTitle}
      .description=${this.noKeysDescription}
      .buttonText=${this.noKeysButtonText}
      .reason=${this.aiStatusReason}
      .isOwner=${this.aiStatusIsOwner}
      .onOpenSettings=${this.onOpenSettings}
    ></ai-no-keys-card>`;
  }

  private readonly _onScroll = () => {
    const { clientHeight, scrollTop, scrollHeight } = this;
    const previousTop = this._lastScrollTop;
    this._lastScrollTop = scrollTop;
    if (
      this._pendingBottomJump ||
      this._restoringScrollAnchor ||
      this.isHistoryLoading
    ) {
      return;
    }

    if (scrollTop < LOAD_MORE_SCROLL_THRESHOLD) {
      this._loadPreviousWindow();
    }

    const following = followsAfterScroll(this.following, {
      previousTop,
      top: scrollTop,
      distanceFromEnd: scrollHeight - scrollTop - clientHeight,
    });
    if (following) this.following = true;
    else this._stopFollowing();
  };

  /**
   * A wheel turned up is the reader leaving the end. It has to win over a
   * follow step that is still moving the history the other way.
   */
  private readonly _onWheel = (event: WheelEvent) => {
    if (event.deltaY >= 0 || !this.following || this.scrollTop <= 0) return;
    for (const node of event.composedPath()) {
      if (node === this) break;
      // A scrolled box inside a message takes the wheel itself.
      if (node instanceof Element && node.scrollTop > 0) return;
    }
    this._stopFollowing();
  };

  /** A finger on the history holds it still until it lifts. */
  private readonly _onTouchStart = () => {
    this._touching = true;
    streamLainAnimationController.cancel(this);
  };

  private readonly _onTouchEnd = () => {
    this._touching = false;
  };

  private _stopFollowing() {
    if (!this.following) return;
    this.following = false;
    streamLainAnimationController.cancel(this);
  }

  private readonly _onDownIndicatorClick = () => {
    this.scrollToEnd();
  };

  private _syncEndButton(show: boolean) {
    if (show === this._endButtonShown) return;
    this._endButtonShown = show;
    if (show) {
      this._endButtonMounted = true;
      this.updateComplete
        .then(() => {
          const button = this.querySelector('.down-indicator');
          if (!button || !this._endButtonShown) return;
          const { from, to } = streamLainOverlayKeyframes('bottom', 'enter');
          streamLainAnimationController.play(button, [from, to], 'enter');
        })
        .catch(console.error);
      return;
    }

    const button = this.querySelector('.down-indicator');
    if (!button) {
      this._endButtonMounted = false;
      return;
    }
    const { from, to } = streamLainOverlayKeyframes('bottom', 'exit');
    streamLainAnimationController
      .play(button, [from, to], 'exit')
      .finished.then(outcome => {
        if (outcome === 'finished' && !this._endButtonShown) {
          this._endButtonMounted = false;
        }
      })
      .catch(console.error);
  }

  protected override render() {
    const { status, error } = this.chatContextValue;
    const { isHistoryLoading } = this;

    // sl-canvas2-8.31: Determine if we're in loading state (for "AI is generating" animation)
    const isLoading =
      status === 'loading' || status === 'transmitting' || status === 'error';

    // sl-canvas2-8.31: Filter out system messages, but keep last empty message during loading
    // We need to filter with awareness of position and loading state
    const filteredItems = this._filterMessages(isLoading);
    const { awaitingConfirmation } = this.chatContextValue;
    this._syncVisibleWindow(filteredItems.length);
    const visibleStartIndex = this._visibleStartIndex;
    const visibleItems = filteredItems.slice(visibleStartIndex);

    const goalActivity = this.goalPlan
      ? goalActivityText({
          tree: this.goalPlan.goalTree,
          status: this.goalPlan.status,
        })
      : null;
    const goalIsRunning = isGoalBusy(this.goalPlan?.status);

    return html`
      <div
        class=${classMap({
          'chat-panel-messages-container': true,
          'independent-mode': !!this.independentMode,
        })}
        data-testid="chat-panel-messages-container"
      >
        ${filteredItems.length === 0
          ? html`<div
              class="messages-placeholder"
              data-testid="chat-panel-messages-placeholder"
            >
              ${this.hasKeys === false && !isHistoryLoading
                ? this._renderNoKeysCardIfNeeded()
                : html`
                    ${renderAIChatEmptyStateContent({
                      isLoading: isHistoryLoading,
                      panelTestIds: true,
                    })}
                  `}
            </div> `
          : html`
              ${visibleStartIndex > 0
                ? html`<div class="chat-history-window-sentinel"></div>`
                : nothing}
              ${repeat(
                visibleItems,
                (item, index) =>
                  this._messageKeys.keyOf(item, visibleStartIndex + index),
                (item, index) => {
                  const filteredIndex = visibleStartIndex + index;
                  const isLast = filteredIndex === filteredItems.length - 1;
                  if (isChatMessage(item) && item.role === 'user') {
                    return html`<chat-message-user
                      .item=${item}
                      .canvasAIChatContextMode=${this.canvasAIChatContextMode}
                    ></chat-message-user>`;
                  } else if (isChatMessage(item) && item.role === 'assistant') {
                    return html`<chat-message-assistant
                      .host=${this.host}
                      .workspaceId=${this.workspaceId}
                      .session=${this.session}
                      .item=${item}
                      .isLast=${isLast}
                      .status=${isLast ? status : 'idle'}
                      .awaitingConfirmation=${isLast && awaitingConfirmation}
                      .error=${isLast ? error : null}
                      .extensions=${this.extensions}
                      .affineFeatureFlagService=${this.affineFeatureFlagService}
                      .affineThemeService=${this.affineThemeService}
                      .notificationService=${this.notificationService}
                      .retry=${() => this.retry()}
                      .independentMode=${this.independentMode}
                      .docDisplayService=${this.docDisplayService}
                      .codeSelections=${this.chatContextValue.codeSelections ||
                      []}
                      .peekViewService=${this.peekViewService}
                      .onOpenDoc=${this.onOpenDoc}
                      .workspaceDocSyncEngine=${this.workspaceDocSyncEngine}
                      .workspaceNavigationPort=${this.workspaceNavigationPort}
                      .onOpenSettings=${this.aiStatusIsOwner
                        ? this.onOpenSettings
                        : undefined}
                    ></chat-message-assistant>`;
                  } else if (isChatAction(item) && this.host) {
                    return html`<chat-message-action
                      .host=${this.host}
                      .item=${item}
                    ></chat-message-action>`;
                  }
                  return nothing;
                }
              )}
            `}
        ${goalActivity
          ? html`<div
              class="goal-activity"
              data-testid="chat-goal-activity"
              role="status"
              aria-live="polite"
            >
              ${renderAssistantIdentity(
                goalIsRunning ? 'transmitting' : 'idle'
              )}
              <div class="goal-activity-status">
                ${goalIsRunning
                  ? html`<span class="goal-activity-dots" aria-hidden="true"
                      ><i></i><i></i><i></i
                    ></span>`
                  : nothing}
                <span>${goalActivity}</span>
              </div>
            </div>`
          : nothing}
        ${this._renderNoKeysCardForExistingThread()}
      </div>
      ${this._endButtonMounted
        ? html`<div class="chat-end-anchor">
            <button
              type="button"
              class="down-indicator"
              data-testid="chat-panel-scroll-down-indicator"
              aria-label=${I18n.t('com.streamlain.ai.chat.scroll-to-end')}
              @click=${this._onDownIndicatorClick}
            >
              ${ArrowDownIcon()}
            </button>
          </div>`
        : nothing}
    `;
  }

  override connectedCallback() {
    super.connectedCallback();
    const { disposables } = this;

    // Check if AI keys are configured
    this._checkAIKeys().catch(console.error);

    Promise.resolve(AIProvider.userInfo)
      .then(res => {
        this.avatarUrl = res?.avatarUrl ?? '';
      })
      .catch(console.error);

    disposables.add(
      AIProvider.slots.userInfo.subscribe(userInfo => {
        const { status, error } = this.chatContextValue;
        this.avatarUrl = userInfo?.avatarUrl ?? '';
        if (
          status === 'error' &&
          error instanceof UnauthorizedError &&
          userInfo
        ) {
          this.updateContext({ status: 'idle', error: null });
        }
      })
    );

    const selection$ = this.host?.selection.slots.changed;
    if (selection$) {
      disposables.add(
        selection$.subscribe(() => {
          this._selectionValue = this.host?.selection.value ?? [];
        })
      );
    }

    const docModeService = this.host?.std.get(DocModeProvider);
    if (docModeService && this.docId) {
      disposables.add(
        docModeService.onPrimaryModeChange(
          () => this.requestUpdate(),
          this.docId
        )
      );
    }

    // The host element is the scroll container of the history.
    const passive = { passive: true } as const;
    this.addEventListener('scroll', this._onScroll, passive);
    this.addEventListener('wheel', this._onWheel, passive);
    this.addEventListener('touchstart', this._onTouchStart, passive);
    this.addEventListener('touchend', this._onTouchEnd, passive);
    this.addEventListener('touchcancel', this._onTouchEnd, passive);
    disposables.add(() => {
      this.removeEventListener('scroll', this._onScroll);
      this.removeEventListener('wheel', this._onWheel);
      this.removeEventListener('touchstart', this._onTouchStart);
      this.removeEventListener('touchend', this._onTouchEnd);
      this.removeEventListener('touchcancel', this._onTouchEnd);
      streamLainAnimationController.cancel(this);
    });

    disposables.add(() => {
      this._messagesResizeObserver?.disconnect();
      this._messagesResizeObserver = null;
      this._observedMessagesContainer = null;
      if (this._resizeScrollFrame !== null) {
        cancelAnimationFrame(this._resizeScrollFrame);
        this._resizeScrollFrame = null;
      }
    });
  }

  protected override willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);
    if (changedProperties.has('messages')) {
      this._messageKeys.adopt(
        (changedProperties.get('messages') as HistoryMessage[] | undefined) ??
          [],
        this.messages
      );
    }
  }

  protected override updated(_changedProperties: PropertyValues) {
    this._syncMessagesResizeObserver();

    // If we got an AI response, keys must be working - update state
    if (_changedProperties.has('messages') && this.hasKeys === false) {
      const hasAIResponse = this.messages.some(
        m => 'role' in m && m.role === 'assistant'
      );
      if (hasAIResponse) {
        this.hasKeys = true;
      }
    }

    if (_changedProperties.has('isHistoryLoading')) {
      const wasLoading = _changedProperties.get('isHistoryLoading') as boolean;
      // Jump to end (no animation) when history finishes loading
      if (wasLoading && !this.isHistoryLoading && this.messages.length > 0) {
        this._pendingBottomJump = true;
      }
      this.following = true;
    }

    if (_changedProperties.has('chatContextValue')) {
      const prevContext = _changedProperties.get('chatContextValue') as
        | ChatContextValue
        | undefined;
      const { status } = this.chatContextValue;

      if (
        status === 'loading' &&
        prevContext?.status !== 'loading' &&
        prevContext?.status !== 'transmitting'
      ) {
        // A sent message brings the reader to the end and follows the answer.
        this.scrollToEnd();
      } else if (
        status === 'transmitting' ||
        (status === 'success' && prevContext?.status === 'transmitting')
      ) {
        // The answer grows: only a reader who is following moves with it.
        this._followEnd();
      }
    }

    if (this._pendingBottomJump) {
      this._pendingBottomJump = false;
      this._jumpToEnd();
    }

    this._syncEndButton(!this.following && this._lastFilteredItemsLength > 0);
  }

  private _shouldStickToBottomAfterContentResize() {
    return (
      this.messages.length > 0 &&
      this.following &&
      !this._restoringScrollAnchor &&
      !this.isHistoryLoading
    );
  }

  private readonly _scheduleBottomStickAfterContentResize = () => {
    if (!this._shouldStickToBottomAfterContentResize()) return;
    if (this._resizeScrollFrame !== null) return;

    this._resizeScrollFrame = requestAnimationFrame(() => {
      this._resizeScrollFrame = null;
      if (this._shouldStickToBottomAfterContentResize()) {
        this._followEnd();
      }
    });
  };

  private _syncMessagesResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;

    const container = this.querySelector<HTMLElement>(
      '[data-testid="chat-panel-messages-container"]'
    );
    if (!container || container === this._observedMessagesContainer) return;

    this._messagesResizeObserver ??= new ResizeObserver(
      this._scheduleBottomStickAfterContentResize
    );
    this._messagesResizeObserver.disconnect();
    this._messagesResizeObserver.observe(container);
    this._observedMessagesContainer = container;
  }

  /** Brings the newest message into view and follows the conversation again. */
  scrollToEnd() {
    this.following = true;
    requestAnimationFrame(() => this._followEnd('resize'));
  }

  /** Moves a following reader to the end: quick and smooth, never a jump. */
  private _followEnd(kind: 'feedback' | 'resize' = 'feedback') {
    if (!this.following || this._touching) return;
    // A jump to the end is under way and owns the scroll position.
    if (this._endJump.active) return;
    const top = this.scrollHeight - this.clientHeight;
    if (top - this.scrollTop <= CHAT_END_TOLERANCE) return;
    streamLainAnimationController.scrollTo(this, { top }, kind);
  }

  /** Puts a reader back where they were; a following reader stays at the end. */
  scrollToPos(top: number) {
    requestAnimationFrame(() => {
      if (this.following) {
        this._followEnd('resize');
        return;
      }
      this.scrollTo({ top });
    });
  }

  private readonly _syncMissingMessageIds = async (
    sessionId: string
  ): Promise<HistoryMessage[]> => {
    const { messages } = this.chatContextValue;
    if (!AIProvider.histories) return messages;

    const historyIds = await AIProvider.histories.ids(
      this.workspaceId,
      this.docId,
      { sessionId, withMessages: true }
    );
    const persistedMessages = historyIds?.[0]?.messages ?? [];
    const nextMessages = syncMissingMessageIds(messages, persistedMessages);
    if (!nextMessages) return messages;

    this.updateContext({ messages: nextMessages });
    return nextMessages;
  };

  private _showOfflineAIToast() {
    this.notificationService?.notify({
      title: I18n.t('com.streamlain.ai.offline.unavailable'),
      message: I18n.t('com.streamlain.ai.offline.detail'),
      accent: 'warning',
    });
  }

  retry = async () => {
    try {
      if (!getAIChatOnlineStatus()) {
        this._showOfflineAIToast();
        return;
      }

      const sessionId = (await this.createSession())?.sessionId;
      if (!sessionId) return;
      if (!AIProvider.actions.chat) return;

      const syncedMessages = await this._syncMissingMessageIds(sessionId);

      const abortController = new AbortController();
      const messages = [...syncedMessages];
      // user-message that triggered the failed assistant response — its id is
      // the triggerId backend uses to bucket tool-loop sub-calls. Retry without
      // it would force trackStreamUsage(lifecycle='partial') to throw.
      const lastUserMessage = [...messages]
        .reverse()
        .find(m => isChatMessage(m) && m.role === 'user');
      if (!lastUserMessage || !isChatMessage(lastUserMessage)) return;
      if (!lastUserMessage.id) {
        throw new Error('Retry is unavailable until the user message is saved');
      }
      const last = messages[messages.length - 1];
      if ('content' in last) {
        last.content = '';
        last.streamObjects = [];
        last.createdAt = new Date().toISOString();
      }
      this.updateContext({
        messages,
        status: 'loading',
        error: null,
        abortController,
      });

      const retrievalPlan = resolveCopilotRetrievalPlan(
        lastUserMessage.content,
        this.aiToolsConfigService.config.value,
        !!this.networkSearchConfig.visible.value
      );

      const stream = await AIProvider.actions.chat({
        sessionId,
        retry: true,
        messageId: lastUserMessage.id,
        modelId: this.agentChoice.modelId.value,
        docId: this.docId,
        workspaceId: this.workspaceId,
        stream: true,
        signal: abortController.signal,
        where: 'chat-panel',
        control: 'chat-send',
        isRootSession: true,
        reasoning: this._isReasoningActive,
        reasoningEffort: this._selectedReasoningMode,
        webSearch: retrievalPlan.webSearch,
        toolsConfig: retrievalPlan.toolsConfig,
      });

      for await (const text of stream) {
        const messages = this.chatContextValue.messages.slice(0);
        const last = messages.at(-1);
        if (last && isChatMessage(last)) {
          try {
            const parsed = StreamObjectSchema.parse(JSON.parse(text));
            const streamObjects = mergeStreamObjects([
              ...(last.streamObjects ?? []),
              parsed,
            ]);
            messages[messages.length - 1] = {
              ...last,
              streamObjects,
            };
          } catch {
            if (last.streamObjects?.length) {
              // When streamObjects exist (e.g. reasoning blocks), raw text
              // must join them as text-delta — otherwise renderContent()
              // only renders streamObjects and the text content is lost.
              const streamObjects = mergeStreamObjects([
                ...(last.streamObjects ?? []),
                { type: 'text-delta' as const, textDelta: text },
              ]);
              messages[messages.length - 1] = {
                ...last,
                streamObjects,
              };
            } else {
              messages[messages.length - 1] = {
                ...last,
                content: last.content + text,
              };
            }
          }
          this.updateContext({ messages, status: 'transmitting' });
        }
      }

      this.updateContext({ status: 'success' });
    } catch (error) {
      this.updateContext({ status: 'error', error: error as AIError });
    } finally {
      this.updateContext({ abortController: null });
    }
  };
}
